'use client';

/**
 * Analytics utilities for tracking user interactions
 * Uses Vercel Analytics for event tracking
 */

import { track } from '@vercel/analytics';

export type ActivationMilestone =
  | 'bank_connected'
  | 'sales_channel_connected'
  | 'activity_imported'
  | 'first_exception_reviewed'
  | 'first_report_viewed';

export function trackActivationMilestone(
  milestone: ActivationMilestone,
  metadata: { completedAt: string; elapsedMinutes?: number }
) {
  track('activation_milestone', {
    milestone,
    completedAt: metadata.completedAt,
    elapsedMinutes: metadata.elapsedMinutes,
  });
}

export type OnboardingStepAction =
  | 'viewed'
  | 'completed'
  | 'bank_connected'
  | 'bank_skipped';

/** Track movement through the onboarding wizard to find where people stall. */
export function trackOnboardingStep(
  stepId: string,
  action: OnboardingStepAction
) {
  track('onboarding_step', { stepId, action });
}

/**
 * Track a user menu item click
 * @param action - The menu action that was clicked (e.g., 'billing', 'organization')
 * @param location - Where the click occurred (e.g., 'sidebar', 'header')
 * @param tier - The user's subscription tier (e.g., 'free', 'pro')
 */
export function trackUserMenuClick(
  action: string,
  location: 'sidebar' | 'header',
  tier: string = 'unknown'
) {
  track('user_menu_click', {
    action,
    location,
    tier,
    timestamp: new Date().toISOString(),
  });
}

export function trackBetaCodeViewed(metadata?: {
  code?: string;
  location?: string;
  organizationId?: string;
}) {
  track('beta_code_viewed', {
    code: metadata?.code,
    location: metadata?.location,
    organizationId: metadata?.organizationId,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Conversion Funnel Tracking Functions
 * Track user progress through the subscription conversion funnel
 */

export type ConversionFunnelStage =
  | 'landing_page_view'
  | 'pricing_page_view'
  | 'signup_started'
  | 'signup_completed'
  | 'onboarding_started'
  | 'onboarding_plan_viewed'
  | 'onboarding_plan_selected'
  | 'checkout_initiated'
  | 'checkout_completed'
  | 'trial_started'
  | 'trial_converted'
  | 'trial_expired';

export interface ConversionMetadata {
  plan?: 'free' | 'starter' | 'pro';
  billingPeriod?: 'monthly' | 'yearly';
  source?: string; // Where they came from (e.g., 'landing_cta', 'pricing_page', 'trial_banner')
  value?: number; // Monetary value of the conversion
  daysInTrial?: number; // For trial-related events
  userId?: string;
  [key: string]: any;
}

/**
 * Track conversion funnel events
 * @param stage - The funnel stage being tracked
 * @param metadata - Additional context about the event
 */
function trackConversion(
  stage: ConversionFunnelStage,
  metadata?: ConversionMetadata
) {
  track('conversion_funnel', {
    stage,
    ...metadata,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Track when user starts the signup process
 * @param plan - The plan they're signing up for (if pre-selected)
 * @param source - Where they initiated signup from
 */
export function trackSignupStarted(
  plan?: 'free' | 'starter' | 'pro',
  source?: string
) {
  trackConversion('signup_started', { plan, source });
}
